(function exposeLiveStreamFlashOffers(root, factory) {
  const api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  else {
    root.WYWLiveStreamFlashOffers = api;
    if (!root.document || root.WYWLiveStreamFlashOffersInitialized) return;
    root.WYWLiveStreamFlashOffersInitialized = true;
    const initialize = () => api.initializeFlashOfferSections(root.document);
    if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', initialize, { once: true });
    else initialize();
    root.document.addEventListener('shopify:section:load', event => api.initializeFlashOfferSections(event.target));
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function buildLiveStreamFlashOffers(browserRoot) {
  function normalizeId(value) {
    if (typeof value !== 'number' && typeof value !== 'string') return null;
    const id = Number(value);
    return Number.isInteger(id) && id > 0 ? id : null;
  }

  function normalizeIdList(values) {
    if (!Array.isArray(values) || values.length === 0) return null;
    const ids = values.map(normalizeId);
    return ids.every(id => id !== null) ? ids : null;
  }

  function isFiniteInteger(value) {
    return Number.isFinite(value) && Number.isInteger(value);
  }

  function normalizeEligibility(campaign) {
    const source = campaign?.eligibilitySource || 'products';
    if (source === 'products') {
      const productIds = normalizeIdList(campaign?.eligibleProductIds);
      const variantIds = normalizeIdList(campaign?.eligibleVariantIds);
      return productIds && variantIds ? { source, productIds, variantIds, collection: null } : null;
    }
    if (source !== 'collection') return null;
    const collection = campaign?.eligibleCollection;
    if (!collection || typeof collection !== 'object' || Array.isArray(collection)) return null;
    const id = normalizeId(collection.id);
    const handle = typeof collection.handle === 'string' ? collection.handle.trim() : '';
    const url = typeof collection.url === 'string' ? collection.url.trim() : '';
    const productCount = collection.productCount;
    const productIds = normalizeIdList(collection.productIds);
    const variantIds = normalizeIdList(collection.variantIds);
    return id !== null
      && handle !== ''
      && url !== ''
      && isFiniteInteger(productCount)
      && productCount > 0
      && productCount <= 250
      && productIds
      && variantIds
      ? {
        source,
        productIds,
        variantIds,
        collection: { ...collection, id, handle, url, productCount, productIds, variantIds },
      }
      : null;
  }

  function selectActiveCampaign(campaigns) {
    const configured = Array.isArray(campaigns) ? campaigns : [];
    if (configured.some(item => !item || typeof item !== 'object' || Array.isArray(item))) {
      return { campaign: null, reason: 'invalid' };
    }
    const active = configured.filter(item => item.enabled === true);
    if (active.length === 0) return { campaign: null, reason: 'off' };
    if (active.length !== 1) return { campaign: null, reason: 'ambiguous' };
    const campaign = active[0];
    const rewardProductId = normalizeId(campaign.reward?.productId);
    const normalizedEligibility = normalizeEligibility(campaign);
    const eligibleProductIds = normalizedEligibility?.source === 'collection'
      ? normalizedEligibility.productIds.filter(id => id !== rewardProductId)
      : normalizedEligibility?.productIds;
    const eligibility = normalizedEligibility && eligibleProductIds?.length > 0
      ? {
        ...normalizedEligibility,
        productIds: eligibleProductIds,
        collection: normalizedEligibility.collection
          ? { ...normalizedEligibility.collection, productIds: eligibleProductIds }
          : null,
      }
      : null;
    const hasValidThreshold = campaign.qualificationMode === 'amount'
      ? isFiniteInteger(campaign.requiredAmountCents) && campaign.requiredAmountCents > 0
      : campaign.qualificationMode === 'quantity'
        && isFiniteInteger(campaign.requiredQuantity)
        && campaign.requiredQuantity > 0;
    return getRewardOptions(campaign).length > 0
      && typeof campaign.id === 'string'
      && campaign.id.trim() !== ''
      && eligibility
      && campaign.reward
      && rewardProductId !== null
      && isFiniteInteger(campaign.requiredQuantity)
      && isFiniteInteger(campaign.requiredAmountCents)
      && isFiniteInteger(campaign.reward.quantity)
      && campaign.reward.quantity >= 1
      && campaign.reward.quantity <= 5
      && (eligibility.source === 'collection' || !eligibility.productIds.includes(rewardProductId))
      && hasValidThreshold
      ? {
        campaign: {
          ...campaign,
          eligibilitySource: eligibility.source,
          eligibleProductIds: eligibility.productIds,
          eligibleVariantIds: eligibility.variantIds,
          eligibleCollection: eligibility.collection,
          reward: { ...campaign.reward, productId: rewardProductId },
        },
        reason: null,
      }
      : { campaign: null, reason: 'invalid' };
  }

  function getRewardOptions(campaign) {
    const variants = Array.isArray(campaign?.reward?.variants) ? campaign.reward.variants : [];
    const normalizedVariants = variants.map(variant => {
      const id = normalizeId(variant?.id);
      return id === null || !variant || typeof variant !== 'object' || Array.isArray(variant)
        ? null
        : { ...variant, id };
    });
    if (normalizedVariants.some(variant => variant === null)) return [];
    if (campaign?.reward?.mode === 'fixed') {
      const onePackVariant = normalizedVariants.find(variant => (
        variant.title === '1 Pack' && variant.available === true
      ));
      return onePackVariant ? [onePackVariant] : [];
    }
    const available = normalizedVariants.filter(variant => variant.available === true);
    return campaign?.reward?.mode === 'choice' && available.length > 0 && available.length <= 6 ? available : [];
  }

  function getEligibleTotals(cart, campaign) {
    const eligibleProductIds = normalizeIdList(campaign?.eligibleProductIds);
    if (!eligibleProductIds) return { quantity: 0, amountCents: 0 };
    return (cart?.items || []).reduce((totals, item) => {
      if (!eligibleProductIds.includes(Number(item.product_id))) return totals;
      totals.quantity += Number(item.quantity) || 0;
      totals.amountCents += Number(item.final_line_price) || 0;
      return totals;
    }, { quantity: 0, amountCents: 0 });
  }

  function evaluateOffer({ campaigns, cart, triggerVariantId, claimed }) {
    const selection = selectActiveCampaign(campaigns);
    if (!selection.campaign) return { eligible: false, reason: selection.reason };
    const campaign = selection.campaign;
    const triggerId = normalizeId(triggerVariantId);
    const isXTrigger = triggerId !== null
      && campaign.eligibleVariantIds.includes(triggerId)
      && (cart?.items || []).some(item => (
        campaign.eligibleProductIds.includes(Number(item.product_id))
        && Number(item.variant_id) === triggerId
      ));
    if (!isXTrigger) return { eligible: false, reason: 'non-x-trigger' };
    if (claimed) return { eligible: false, reason: 'claimed' };
    if ((cart?.items || []).some(item => Number(item.product_id) === Number(campaign.reward.productId))) {
      return { eligible: false, reason: 'reward-present' };
    }
    const totals = getEligibleTotals(cart, campaign);
    const qualified = campaign.qualificationMode === 'amount'
      ? totals.amountCents >= Number(campaign.requiredAmountCents)
      : totals.quantity >= Number(campaign.requiredQuantity);
    if (!qualified) return { eligible: false, reason: 'threshold' };
    return { eligible: true, campaign, rewardVariants: getRewardOptions(campaign), totals };
  }

  async function fetchSectionPayload(url) {
    const response = await fetch(url, { headers: { Accept: 'text/html' }, cache: 'no-store' });
    if (!response.ok) throw new Error(`Flash offer section refresh failed: ${response.status}`);
    const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
    const config = documentFragment.querySelector('[data-flash-offer-config]');
    if (!config) throw new Error('Flash offer section refresh returned no configuration');
    return JSON.parse(config.textContent);
  }

  const defaultDependencies = {
    async fetchSectionConfig(root) {
      const url = new URL(root.dataset.sectionUrl || window.location.pathname, window.location.origin);
      url.searchParams.set('section_id', root.dataset.sectionId);
      url.searchParams.set('_flash_offer', String(Date.now()));
      const payload = await fetchSectionPayload(url);
      const campaigns = Array.isArray(payload) ? payload : payload?.campaigns;
      const active = Array.isArray(campaigns) ? campaigns.filter(campaign => campaign?.enabled === true) : [];
      if (active.length !== 1 || active[0].eligibilitySource !== 'collection') return payload;
      const collection = active[0].eligibleCollection;
      if (normalizeIdList(collection?.productIds) && normalizeIdList(collection?.variantIds)) return payload;
      if (typeof collection?.url !== 'string' || collection.url.trim() === '') return payload;
      const collectionUrl = new URL(collection.url, window.location.origin);
      collectionUrl.searchParams.set('section_id', root.dataset.sectionId);
      collectionUrl.searchParams.set('_flash_offer', String(Date.now()));
      return fetchSectionPayload(collectionUrl);
    },
    async fetchCart() {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error(`Cart refresh failed: ${response.status}`);
      return response.json();
    },
    async addReward({ id, quantity }) {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity }] }),
      });
      if (!response.ok) throw new Error(`Reward add failed: ${response.status}`);
      return response.json();
    },
  };

  function createDefaultDependencies(root) {
    const globalWindow = browserRoot?.window || browserRoot;
    const documentObject = globalWindow?.document;
    const findHook = selector => root.querySelector?.(selector) || documentObject?.querySelector?.(selector);
    const hideRebuy = () => globalWindow?.Rebuy?.SmartCart?.hide?.();
    const claimKey = (cartToken, campaignId) => `wyw-flash-offer:${cartToken}:${campaignId}`;

    function setText(selector, value, hideWhenEmpty = false) {
      const element = findHook(selector);
      if (!element) return;
      element.textContent = value || '';
      if (hideWhenEmpty) element.hidden = !value;
    }

    function retryAfterAlpineUpdate(callback) {
      const retryOnFrame = () => {
        if (!callback()) globalWindow?.requestAnimationFrame?.(callback);
      };
      if (globalWindow?.queueMicrotask) globalWindow.queueMicrotask(retryOnFrame);
      else globalWindow?.requestAnimationFrame?.(callback);
    }

    function updateVariantSelection(variantId, campaign) {
      const selectedId = String(variantId);
      const selectedVariant = campaign.reward.variants.find(variant => String(variant.id) === selectedId);
      const options = findHook('[data-flash-offer-variant-options]');
      options?.querySelectorAll?.('[data-flash-offer-variant-id]').forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.flashOfferVariantId === selectedId));
      });
      const accept = findHook('[data-flash-offer-accept]');
      if (!accept) return;
      accept.dataset.flashOfferVariantId = selectedId;
      const label = accept.querySelector?.('.push-btn__surface') || accept;
      const defaultCta = campaign.reward.mode === 'choice' && selectedVariant?.title
        ? `Add ${campaign.reward.productTitle} (${selectedVariant.title}) to my cart`
        : `Add ${campaign.reward.productTitle} to my cart`;
      label.textContent = campaign.copy?.cta || defaultCta;
    }

    function renderCampaign(campaign, rewardVariants) {
      const render = () => {
        const dialog = findHook('[data-flash-offer-dialog]');
        if (!dialog) return false;
        setText('[data-flash-offer-badge]', campaign.copy?.badge, true);
        setText('[data-flash-offer-headline]', campaign.copy?.headline || `You unlocked ${campaign.reward.productTitle}!`);
        setText('[data-flash-offer-body]', campaign.copy?.body || 'Add it now and your automatic discount will appear in cart.');
        setText('[data-flash-offer-urgency]', campaign.copy?.urgency, true);

        const image = findHook('[data-flash-offer-image]');
        if (image) {
          image.hidden = !campaign.reward.imageUrl;
          image.alt = campaign.reward.productTitle || '';
          if (campaign.reward.imageUrl) image.src = campaign.reward.imageUrl;
          else image.removeAttribute?.('src');
        }

        const fieldset = findHook('[data-flash-offer-variants]');
        const options = findHook('[data-flash-offer-variant-options]');
        const isChoice = campaign.reward.mode === 'choice';
        if (fieldset) fieldset.hidden = !isChoice;
        options?.replaceChildren?.();
        if (isChoice && options && documentObject) {
          rewardVariants.forEach((variant, index) => {
            const button = documentObject.createElement('button');
            button.type = 'button';
            button.textContent = variant.title;
            button.dataset.flashOfferVariantId = String(variant.id);
            button.setAttribute('aria-pressed', String(index === 0));
            options.append(button);
          });
        }
        updateVariantSelection(rewardVariants[0]?.id, campaign);
        return true;
      };

      if (!render()) retryAfterAlpineUpdate(render);
    }

    return {
      ...defaultDependencies,
      eventTarget: documentObject,
      getClaim(cartToken, campaignId) {
        try {
          return globalWindow?.sessionStorage?.getItem(claimKey(cartToken, campaignId)) === '1';
        } catch (_) {
          return false;
        }
      },
      setClaim(cartToken, campaignId) {
        try {
          globalWindow?.sessionStorage?.setItem(claimKey(cartToken, campaignId), '1');
        } catch (_) {
          // Storage can be unavailable in privacy modes; the cart operation still succeeded.
        }
      },
      hideRebuy,
      showRebuy: () => globalWindow?.Rebuy?.SmartCart?.show?.(),
      beginRebuySuppression: () => documentObject?.addEventListener?.('rebuy:smartcart.show', hideRebuy),
      endRebuySuppression: () => documentObject?.removeEventListener?.('rebuy:smartcart.show', hideRebuy),
      isPopupReady: () => globalWindow?.Alpine?.store?.('modals')?.isRegistered?.('liveStreamFlashOffer') === true,
      openPopup: () => globalWindow.Alpine.store('modals').open('liveStreamFlashOffer'),
      closePopup: () => globalWindow?.Alpine?.store?.('modals')?.close?.('liveStreamFlashOffer'),
      renderCampaign,
      updateVariantSelection,
      setError(message) {
        const update = () => {
          const error = findHook('[data-flash-offer-error]');
          if (!error) return false;
          error.textContent = message || '';
          error.hidden = !message;
          return true;
        };
        if (!update() && message) retryAfterAlpineUpdate(update);
      },
      setLoading(value) {
        const accept = findHook('[data-flash-offer-accept]');
        if (!accept) return;
        const label = accept.querySelector?.('.push-btn__surface') || accept;
        accept.disabled = value;
        findHook('[data-flash-offer-variant-options]')?.querySelectorAll?.('[data-flash-offer-variant-id]').forEach(button => {
          button.disabled = value;
        });
        if (value) {
          if (!Object.prototype.hasOwnProperty.call(accept.dataset, 'flashOfferIdleLabel')) {
            accept.dataset.flashOfferIdleLabel = label.textContent;
          }
          accept.setAttribute('aria-busy', 'true');
          label.textContent = 'Adding reward…';
          return;
        }
        accept.removeAttribute('aria-busy');
        if (Object.prototype.hasOwnProperty.call(accept.dataset, 'flashOfferIdleLabel')) {
          label.textContent = accept.dataset.flashOfferIdleLabel;
          delete accept.dataset.flashOfferIdleLabel;
        }
      },
      redirect: url => globalWindow?.location?.assign?.(url),
      warn: (message, error) => globalWindow?.console?.warn?.(`[Live stream flash offers] ${message}`, error),
    };
  }

  function createFlashOfferController(root, injectedDependencies = {}) {
    const dependencies = { ...createDefaultDependencies(root), ...injectedDependencies };
    const eventTarget = dependencies.eventTarget;
    let pendingEvaluation = null;
    let currentOffer = null;
    let selectedVariantId = null;
    let addingReward = false;
    let suppressionActive = false;
    let destroyed = false;

    const runCleanup = (callback, message) => {
      try {
        callback();
      } catch (error) {
        try {
          dependencies.warn(message, error);
        } catch (_) {
          // Cleanup must continue even if the diagnostic adapter is unavailable.
        }
      }
    };

    const dismiss = (options = {}) => {
      if (!currentOffer && !suppressionActive) return;
      currentOffer = null;
      selectedVariantId = null;
      const wasSuppressed = suppressionActive;
      suppressionActive = false;
      if (!options.fromClosedEvent) {
        runCleanup(dependencies.closePopup, 'Unable to close the offer popup during cleanup.');
      }
      if (wasSuppressed) {
        runCleanup(dependencies.endRebuySuppression, 'Unable to end Rebuy suppression during cleanup.');
        runCleanup(dependencies.showRebuy, 'Unable to restore Rebuy during cleanup.');
      }
    };

    const handleCartAction = action => {
      if (destroyed || currentOffer) return Promise.resolve();
      if (pendingEvaluation) return pendingEvaluation;
      pendingEvaluation = (async () => {
        try {
          const [freshConfig, cart] = await Promise.all([
            dependencies.fetchSectionConfig(root),
            dependencies.fetchCart(),
          ]);
          if (destroyed) return;
          const campaigns = Array.isArray(freshConfig) ? freshConfig : freshConfig?.campaigns;
          const selected = selectActiveCampaign(campaigns);
          let claimed = false;
          if (selected.campaign) {
            try {
              claimed = dependencies.getClaim(cart?.token, selected.campaign.id);
            } catch (error) {
              dependencies.warn('Session claim storage is unavailable.', error);
            }
          }
          const result = evaluateOffer({
            campaigns,
            cart,
            triggerVariantId: action?.variantId,
            claimed,
          });
          if (!result.eligible || destroyed) return;
          if (!dependencies.isPopupReady()) return;
          currentOffer = {
            campaign: result.campaign,
            rewardVariants: result.rewardVariants,
            cartToken: cart?.token,
          };
          selectedVariantId = result.rewardVariants[0]?.id || null;
          dependencies.setError('');
          dependencies.renderCampaign(result.campaign, result.rewardVariants);
          dependencies.hideRebuy();
          dependencies.beginRebuySuppression();
          suppressionActive = true;
          const openedOffer = currentOffer;
          const rollbackOpen = error => {
            dependencies.warn('Unable to open the offer.', error);
            if (currentOffer === openedOffer) dismiss();
          };
          try {
            const opening = dependencies.openPopup();
            if (opening === false) dismiss();
            else if (opening?.then) opening.catch(rollbackOpen);
          } catch (error) {
            rollbackOpen(error);
          }
        } catch (error) {
          dependencies.warn('Unable to evaluate an offer.', error);
        }
      })().finally(() => {
        pendingEvaluation = null;
      });
      return pendingEvaluation;
    };

    const accept = async variantId => {
      if (!currentOffer || addingReward || destroyed) return;
      const rewardId = normalizeId(variantId ?? selectedVariantId);
      if (!currentOffer.rewardVariants.some(variant => variant.id === rewardId)) return;
      const acceptedOffer = currentOffer;
      addingReward = true;
      dependencies.setError('');
      dependencies.setLoading(true);
      try {
        await dependencies.addReward({ id: rewardId, quantity: acceptedOffer.campaign.reward.quantity });
        try {
          dependencies.setClaim(acceptedOffer.cartToken, acceptedOffer.campaign.id);
        } catch (error) {
          dependencies.warn('Session claim storage is unavailable.', error);
        }
        dependencies.redirect(root.dataset.cartUrl || '/cart');
      } catch (error) {
        dependencies.warn('Unable to add the reward.', error);
        if (currentOffer === acceptedOffer && !destroyed) {
          dependencies.setError("We couldn't add your reward. Please try again.");
        }
      } finally {
        addingReward = false;
        dependencies.setLoading(false);
      }
    };

    const onDocumentClick = event => {
      const close = event.target?.closest?.('[data-flash-offer-close], [data-flash-offer-decline]');
      if (close) {
        event.preventDefault?.();
        dismiss();
        return;
      }
      const acceptButton = event.target?.closest?.('[data-flash-offer-accept]');
      if (acceptButton) {
        event.preventDefault?.();
        accept(selectedVariantId);
        return;
      }
      const variant = event.target?.closest?.('[data-flash-offer-variant-id]');
      if (variant && currentOffer && !addingReward) {
        const variantId = normalizeId(variant.dataset.flashOfferVariantId);
        if (currentOffer.rewardVariants.some(option => option.id === variantId)) {
          selectedVariantId = variantId;
          dependencies.updateVariantSelection?.(variantId, currentOffer.campaign);
        }
        return;
      }
    };
    const onProductAdd = event => handleCartAction({ type: 'add', variantId: event.detail?.variantId });
    const onRebuyCartAdd = async event => {
      const payload = event.detail?.item;
      const items = Array.isArray(payload?.items) ? payload.items : [payload];
      for (const item of items) {
        await handleCartAction({ type: 'add', variantId: item?.variant_id ?? item?.id });
        if (currentOffer || destroyed) break;
      }
    };
    const onLineItemChange = event => {
      const quantity = event.detail?.quantity;
      const previousQuantity = event.detail?.previousQuantity;
      if (!Number.isFinite(quantity) || !Number.isFinite(previousQuantity) || quantity <= previousQuantity) return;
      handleCartAction({ type: 'change', variantId: event.detail?.variantId });
    };
    const onPopupClosed = () => dismiss({ fromClosedEvent: true });

    eventTarget?.addEventListener?.('click', onDocumentClick);
    eventTarget?.addEventListener?.('theme:product:add', onProductAdd);
    eventTarget?.addEventListener?.('rebuy:cart.add', onRebuyCartAdd);
    eventTarget?.addEventListener?.('theme:line-item:change', onLineItemChange);
    eventTarget?.addEventListener?.('live-stream-flash-offer-is-closed', onPopupClosed);

    return {
      handleCartAction,
      accept,
      dismiss,
      destroy() {
        if (destroyed) return;
        destroyed = true;
        eventTarget?.removeEventListener?.('click', onDocumentClick);
        eventTarget?.removeEventListener?.('theme:product:add', onProductAdd);
        eventTarget?.removeEventListener?.('rebuy:cart.add', onRebuyCartAdd);
        eventTarget?.removeEventListener?.('theme:line-item:change', onLineItemChange);
        eventTarget?.removeEventListener?.('live-stream-flash-offer-is-closed', onPopupClosed);
        dismiss({ force: true });
      },
    };
  }

  const sectionControllers = new Map();

  function initializeFlashOfferSections(scope = browserRoot?.document) {
    if (!scope) return [];
    const roots = [];
    if (scope.matches?.('[data-live-stream-flash-offers]')) roots.push(scope);
    scope.querySelectorAll?.('[data-live-stream-flash-offers]').forEach(root => roots.push(root));
    return roots.map(root => {
      const sectionId = root.dataset.sectionId;
      const existing = sectionControllers.get(sectionId);
      if (existing?.root === root) return existing.controller;
      existing?.controller.destroy();
      const controller = createFlashOfferController(root);
      sectionControllers.set(sectionId, { root, controller });
      return controller;
    });
  }

  return { selectActiveCampaign, getRewardOptions, getEligibleTotals, evaluateOffer, createFlashOfferController, initializeFlashOfferSections };
});
